import { supabase } from '../../config/supabase';

/**
 * Insert a new entry into job_action_logs.
 * Fire-and-forget safe — errors are only logged to console.
 *
 * @param {object} params
 * @param {string}   params.job_id          - jobs.id UUID
 * @param {string}   params.user_id         - users.id UUID
 * @param {string}   params.user_name       - display name
 * @param {string}   params.user_role       - user_role enum value
 * @param {string}   params.action          - short action key e.g. "reschedule"
 * @param {string}   [params.details]       - human-readable detail sentence
 * @param {string}   [params.quote_type]    - quote_type enum value, if applicable
 * @param {string}   [params.action_origin] - e.g. "schedule-roof", "schedule-delivery"
 * @param {string[]} [params.changed_fields]
 * @param {object}   [params.old_values]
 * @param {object}   [params.new_values]
 */
export const createJobActionLog = async ({
    job_id,
    user_id,
    user_name,
    user_role,
    action,
    details = null,
    quote_type = null,
    action_origin = null,
    changed_fields = null,
    old_values = null,
    new_values = null,
    company_id
}) => {
    const { error } = await supabase.from('job_action_logs').insert({
        job_id,
        user_id,
        user_name,
        user_role,
        action,
        details,
        quote_type: quote_type || null,
        action_origin,
        changed_fields: changed_fields || null,
        old_values: old_values || null,
        new_values: new_values || null,
        company_id
    });
    if (error) console.error('[activityLog] Failed to insert job_action_log:', error);
};

/**
 * Fetch the most recent logs for a given action_origin, oldest-first
 * (chat order: new messages appear at the bottom).
 *
 * @param {string} actionOrigin
 * @param {number} [limit=60]
 * @returns {Promise<Array>}
 */
export const getRecentActivityLogs = async (actionOrigin, limit = 60, companyId) => {
    const { data, error } = await supabase
        .from('job_action_logs')
        .select('id, job_id, user_name, user_role, action, details, quote_type, timestamp')
        .eq('action_origin', actionOrigin)
        .eq('company_id', companyId)
        .order('timestamp', { ascending: false })
        .limit(limit);

    if (error) {
        console.error('[activityLog] Failed to fetch activity logs:', error);
        return [];
    }
    // Reverse so oldest is at top, newest at bottom (chat style)
    return (data || []).reverse();
};

/**
 * Subscribe to new INSERT events on job_action_logs for a given action_origin.
 *
 * @param {string} actionOrigin
 * @param {function} callback  called with the new row object on INSERT
 * @returns {object} Supabase RealtimeChannel — call .unsubscribe() to clean up
 */
export const subscribeToActivityLogs = (actionOrigin, companyId, callback) => {
    const channel = supabase
        .channel(`activity-log-${actionOrigin}-${companyId}-${Date.now()}`)
        .on(
            'postgres_changes',
            {
                event: 'INSERT',
                schema: 'public',
                table: 'job_action_logs',
                filter: `action_origin=eq.${actionOrigin}`,
            },
            (payload) => {
                const row = payload.new;

                if (row.company_id !== companyId) return;

                callback(row);
            }
        )
        .subscribe();
    return channel;
};
